import { SITE_URL } from '@/lib/utils';
import { useSite } from './SiteProvider';

interface SeoProps {
  /** Page title without the site suffix. Omit on the home page. */
  title?: string;
  description?: string;
  /** Path for the canonical URL, e.g. "/about". */
  path: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}

/** Per-page metadata. React 19 hoists <title>, <meta> and <link> into <head>. */
export function Seo({ title, description, path, image, noindex, jsonLd }: SeoProps) {
  const { settings } = useSite();
  const { seo } = settings;
  const fullTitle = title ? seo.titleTemplate.replace('%s', title) : `${seo.defaultTitle} | ${seo.siteName}`;
  const desc = description || seo.defaultDescription;
  const canonical = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
  const ogImage = image ? new URL(image, SITE_URL).toString() : seo.ogImageUrl || undefined;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }} />
      )}
    </>
  );
}
